import { getAuthHeaders } from "../lib/auth";
import { useState, useEffect, useCallback, useRef } from "react";
import LogLayout from "../components/LogLayout";
import SectionTabs, { INVOICES_TABS } from "../components/SectionTabs";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../App";
import { FileText, Plus, Search, Eye, Trash2, X, Upload, Camera, Image, Download, CreditCard, Receipt, Scan, CheckCircle, AlertCircle, Loader2, Sparkles, QrCode, FileSpreadsheet, ShieldAlert, ShieldCheck, TriangleAlert, ArrowRight, FolderOpen, File as FileIcon, RefreshCw } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const STATUSES = [
  { value: "editee", label: "Éditée", color: "bg-blue-100 text-blue-700 border-blue-200" },
  { value: "received", label: "Reçue/Éditée", color: "bg-blue-100 text-blue-700 border-blue-200" },
  { value: "deposee", label: "Déposée", color: "bg-cyan-100 text-cyan-700 border-cyan-200" },
  { value: "rejetee", label: "Rejetée", color: "bg-red-100 text-red-700 border-red-200" },
  { value: "paiement_partiel", label: "Paiement partiel", color: "bg-amber-100 text-amber-700 border-amber-200" },
  { value: "paiement_effectue", label: "Paiement effectué", color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  { value: "archived", label: "Archivée", color: "bg-slate-100 text-slate-600 border-slate-200" },
];

// Legacy status mapping for display only
const LEGACY_MAP = {
  received: "editee", deposited: "deposee", in_recovery: "paiement_partiel",
  partially_paid: "paiement_partiel", paid: "paiement_effectue"
};

const PHOTO_CATEGORIES = [
  { value: "facture", label: "Facture recapitulative", icon: FileText },
  { value: "depot", label: "Facture cachetée (Dépôt)", icon: Scan },
  { value: "cheque", label: "Chèque", icon: CreditCard },
  { value: "remise", label: "Remise de chèque / Bordereau", icon: Receipt },
];

const formatFCFA = (n) => new Intl.NumberFormat("fr-FR").format(Math.round(n || 0)) + " FCFA";
const getHeaders = getAuthHeaders;
const getStatusStyle = (s) => {
  const mapped = LEGACY_MAP[s] || s;
  return STATUSES.find(st => st.value === mapped)?.color || "bg-slate-500/20 text-slate-400";
};
const getStatusLabel = (s) => {
  const mapped = LEGACY_MAP[s] || s;
  return STATUSES.find(st => st.value === mapped)?.label || s;
};

export default function LogInvoicesPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [invoices, setInvoices] = useState([]);
  const [insurers, setInsurers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [initialDetailAction, setInitialDetailAction] = useState(null); // ex: "archives" pour ouvrir le modal d'import depuis archives
  const [showStatusUpdate, setShowStatusUpdate] = useState(null);
  const [showPhotoUpload, setShowPhotoUpload] = useState(null);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterInsurer, setFilterInsurer] = useState("");
  const [filterPharmacy, setFilterPharmacy] = useState("");
  const [pharmacies, setPharmacies] = useState([]);
  const [search, setSearch] = useState("");
  const [autoUploading, setAutoUploading] = useState(false);
  const [autoDocType, setAutoDocType] = useState("facture");
  const [showAutoDocSelector, setShowAutoDocSelector] = useState(false);
  const autoUploadRef = useRef(null);
  const [form, setForm] = useState({
    insurer_id: "", insurer_name: "", invoice_number: "", patient_name: "",
    amount: "", date: "", due_date: "", description: "",
    period_start: "", period_end: "", affiliations: []
  });

  const load = useCallback(async () => {
    try {
      const h = getHeaders();
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterInsurer) params.insurer_id = filterInsurer;
      if (filterPharmacy) params.pharmacy_id = filterPharmacy;
      const [invRes, insRes] = await Promise.all([
        axios.get(`${API}/log/invoices`, { headers: h, params }),
        axios.get(`${API}/log/insurers`, { headers: h })
      ]);
      setInvoices(invRes.data.invoices || []);
      setInsurers(insRes.data.insurers || []);
      // Load pharmacies list for admin filter
      if (user?.role === "superadmin" || user?.role === "admin") {
        try {
          const pharmRes = await axios.get(`${API}/admin/pharmacies`, { headers: h });
          setPharmacies(pharmRes.data.pharmacies || []);
        } catch {}
      }
    } catch { }
    finally { setLoading(false); }
  }, [filterStatus, filterInsurer, filterPharmacy]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  // Auto-open invoice detail if query params present (ex: depuis Pipeline → Joindre Archives)
  useEffect(() => {
    const openId = searchParams.get("open");
    const action = searchParams.get("action");
    if (openId && !showDetail) {
      axios.get(`${API}/log/invoices/${openId}`, { headers: getHeaders() })
        .then((res) => {
          setShowDetail(res.data.invoice);
          if (action === "archives") setInitialDetailAction("archives");
          setSearchParams({}, { replace: true });
        })
        .catch(() => toast.error("Dossier introuvable"));
    }
  }, [searchParams, showDetail, setSearchParams]);

  const resetForm = () => setForm({
    insurer_id: "", insurer_name: "", invoice_number: "", patient_name: "",
    amount: "", date: "", due_date: "", description: "",
    period_start: "", period_end: "", affiliations: []
  });

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const sel = insurers.find(i => i.id === form.insurer_id);
      const body = {
        ...form,
        amount: parseFloat(form.amount) || 0,
        insurer_name: sel?.name || form.insurer_name,
        affiliations: form.affiliations.map(a => ({ ...a, amount: parseFloat(a.amount) || 0, nb_bons: parseInt(a.nb_bons) || 1 }))
      };
      const res = await axios.post(`${API}/log/invoices`, body, { headers: getHeaders() });
      toast.success("Dossier créé - Ajoutez vos documents");
      setShowCreate(false);
      resetForm();
      load();
      // Open the dossier immediately for document upload
      if (res.data.invoice) {
        setShowDetail(res.data.invoice);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const handleStatusUpdate = async (invoiceId, newStatus) => {
    try {
      await axios.put(`${API}/log/invoices/${invoiceId}/status`, { status: newStatus }, { headers: getHeaders() });
      toast.success("Statut mis a jour");
      setShowStatusUpdate(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cette facture ?")) return;
    try {
      await axios.delete(`${API}/log/invoices/${id}`, { headers: getHeaders() });
      toast.success("Facture supprimee");
      load();
    } catch (e) { toast.error("Erreur suppression"); }
  };

  const loadDetail = async (id) => {
    try {
      const res = await axios.get(`${API}/log/invoices/${id}`, { headers: getHeaders() });
      setShowDetail(res.data.invoice);
    } catch (e) { toast.error("Erreur chargement detail"); }
  };

  // Affiliations management
  const addAffiliation = () => {
    setForm(f => ({ ...f, affiliations: [...f.affiliations, { company_name: "", nb_bons: 1, amount: "" }] }));
  };
  const removeAffiliation = (idx) => {
    setForm(f => ({ ...f, affiliations: f.affiliations.filter((_, i) => i !== idx) }));
  };
  const updateAffiliation = (idx, field, value) => {
    setForm(f => {
      const affs = [...f.affiliations];
      affs[idx] = { ...affs[idx], [field]: value };
      // Auto-calculate total
      const total = affs.reduce((s, a) => s + (parseFloat(a.amount) || 0), 0);
      return { ...f, affiliations: affs, amount: total > 0 ? total.toString() : f.amount };
    });
  };

  const handleQrDownload = async (invoiceId) => {
    try {
      const res = await axios.get(`${API}/log/invoices/${invoiceId}/qr`, {
        headers: getHeaders(), responseType: "blob"
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `qr_${invoiceId}.png`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Erreur QR"); }
  };

  const handleExport = async (format) => {
    try {
      const params = filterStatus ? `?status=${filterStatus}` : "";
      const res = await axios.get(`${API}/log/invoices/export/${format}${params}`, {
        headers: getHeaders(), responseType: "blob"
      });
      const ext = format === "csv" ? "csv" : "xlsx";
      const mime = format === "csv" ? "text/csv" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const blob = new Blob([res.data], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `cmes_log_factures.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Export ${format.toUpperCase()} téléchargé`);
    } catch (e) { toast.error("Erreur export"); }
  };

  const handleAutoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAutoUploading(true);
    setShowAutoDocSelector(false);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_type", autoDocType);
      const res = await axios.post(`${API}/log/invoices/upload-auto`, fd, { headers: { ...getHeaders(), "Content-Type": "multipart/form-data" } });
      const inv = res.data.invoice;
      toast.success(res.data.message);
      load();
      if (inv) setShowDetail(inv);
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur upload"); }
    finally { setAutoUploading(false); e.target.value = ""; }
  };

  const filtered = invoices.filter(inv =>
    !search || inv.invoice_number?.toLowerCase().includes(search.toLowerCase()) ||
    inv.insurer_name?.toLowerCase().includes(search.toLowerCase()) ||
    inv.patient_name?.toLowerCase().includes(search.toLowerCase()) ||
    (inv.affiliations || []).some(a => a.company_name?.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <LogLayout title="Dossiers Factures" subtitle="Vue Liste : gestion des factures récapitulatives d'assurance">
      <SectionTabs tabs={INVOICES_TABS} />
      <div data-testid="log-invoices-page" className="space-y-4">
        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          <div className="flex gap-2 flex-1 w-full sm:w-auto">
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input data-testid="invoice-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher..." className="w-full pl-9 pr-3 py-2 bg-slate-900 border border-white/10 rounded-lg text-sm text-white placeholder:text-slate-500 focus:border-blue-500 focus:outline-none" />
            </div>
            <select data-testid="invoice-filter-status" value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setLoading(true); }} className="bg-slate-900 border border-white/10 rounded-lg text-sm text-slate-300 px-3 py-2 focus:border-blue-500 focus:outline-none">
              <option value="">Tous les statuts</option>
              {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <select data-testid="invoice-filter-insurer" value={filterInsurer} onChange={e => { setFilterInsurer(e.target.value); setLoading(true); }} className="bg-slate-900 border border-white/10 rounded-lg text-sm text-slate-300 px-3 py-2 focus:border-blue-500 focus:outline-none">
              <option value="">Tous les assureurs</option>
              {insurers.map(ins => <option key={ins.id} value={ins.name}>{ins.name} ({ins.invoice_count})</option>)}
            </select>
            {(user?.role === "superadmin" || user?.role === "admin") && pharmacies.length > 0 && (
              <select data-testid="invoice-filter-pharmacy" value={filterPharmacy} onChange={e => { setFilterPharmacy(e.target.value); setLoading(true); }} className="bg-slate-900 border border-white/10 rounded-lg text-sm text-slate-300 px-3 py-2 focus:border-blue-500 focus:outline-none">
                <option value="">Toutes les pharmacies</option>
                {pharmacies.map(p => <option key={p.id} value={p.id}>{p.pharmacy_name || p.full_name}</option>)}
              </select>
            )}
          </div>
          <div className="flex gap-2 flex-wrap relative">
            <input ref={autoUploadRef} type="file" accept="image/*,.pdf" onChange={handleAutoUpload} className="hidden" />
            <div className="relative">
              <Button data-testid="auto-upload-btn" disabled={autoUploading}
                onClick={() => setShowAutoDocSelector(!showAutoDocSelector)}
                className="bg-emerald-600 hover:bg-emerald-500 text-white gap-2">
                {autoUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Scan className="w-4 h-4" />} Importer document (OCR)
              </Button>
              {showAutoDocSelector && (
                <div className="absolute right-0 top-full mt-1 z-50 bg-slate-900 border border-white/10 rounded-xl shadow-2xl p-3 w-64" data-testid="doc-type-selector">
                  <p className="text-xs text-slate-400 mb-2 font-semibold">Type de document :</p>
                  {PHOTO_CATEGORIES.map(cat => (
                    <button key={cat.value} onClick={() => { setAutoDocType(cat.value); autoUploadRef.current?.click(); }}
                      data-testid={`doc-type-${cat.value}`}
                      className={`w-full text-left flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm transition-all mb-1 ${autoDocType === cat.value ? "bg-emerald-600 text-white" : "text-slate-300 hover:bg-white/5"}`}>
                      <cat.icon className="w-4 h-4" /> {cat.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button data-testid="export-csv-btn" onClick={() => handleExport("csv")} className="px-3 py-2 bg-slate-800 hover:bg-slate-700 border border-white/10 rounded-lg text-xs text-slate-300 flex items-center gap-1.5 transition-colors" title="Exporter en CSV">
              <FileSpreadsheet className="w-3.5 h-3.5" /> Exporter CSV
            </button>
            <button data-testid="export-excel-btn" onClick={() => handleExport("excel")} className="px-3 py-2 bg-slate-800 hover:bg-slate-700 border border-white/10 rounded-lg text-xs text-slate-300 flex items-center gap-1.5 transition-colors" title="Exporter en Excel">
              <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-400" /> Exporter Excel
            </button>
            <Button data-testid="create-invoice-btn" onClick={() => setShowCreate(true)} className="bg-blue-600 hover:bg-blue-500/100 text-white gap-2">
              <Plus className="w-4 h-4" /> Nouveau dossier
            </Button>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 bg-slate-900 rounded-xl border border-white/5">
            <FileText className="w-10 h-10 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400 text-sm">Aucune facture trouvee</p>
            <Button data-testid="empty-create-invoice-btn" onClick={() => setShowCreate(true)} variant="outline" className="mt-4 text-blue-400 border-blue-500/30 hover:bg-blue-500/100/10">
              Creer votre premiere facture
            </Button>
          </div>
        ) : (
          <div className="bg-slate-900 rounded-xl border border-white/5 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="invoices-table">
                <thead>
                  <tr className="border-b border-white/5 text-slate-500 text-xs uppercase tracking-wider">
                    <th className="text-left px-4 py-3">N° Facture</th>
                    <th className="text-left px-4 py-3">Assureur</th>
                    <th className="text-center px-4 py-3 hidden md:table-cell">Affiliations</th>
                    <th className="text-center px-4 py-3 hidden md:table-cell">Bons</th>
                    <th className="text-right px-4 py-3">Montant</th>
                    <th className="text-center px-4 py-3 hidden md:table-cell">Photos</th>
                    <th className="text-center px-4 py-3 hidden lg:table-cell">Contrôle</th>
                    <th className="text-center px-4 py-3">Statut</th>
                    <th className="text-center px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(inv => {
                    const recon = inv.reconciliation;
                    const reconStatus = recon?.status;
                    return (
                    <tr key={inv.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors" data-testid={`invoice-row-${inv.id}`}>
                      <td className="px-4 py-3 font-medium text-white">{inv.invoice_number}</td>
                      <td className="px-4 py-3 text-slate-300">{inv.insurer_name}</td>
                      <td className="px-4 py-3 text-center text-slate-400 hidden md:table-cell">{(inv.affiliations || []).length}</td>
                      <td className="px-4 py-3 text-center text-slate-400 hidden md:table-cell">{inv.total_bons || 0}</td>
                      <td className="px-4 py-3 text-right text-white font-medium">{formatFCFA(inv.amount)}</td>
                      <td className="px-4 py-3 text-center hidden md:table-cell">
                        <button onClick={() => loadDetail(inv.id)} className="inline-flex items-center gap-1 text-[10px] text-blue-400 hover:text-blue-300" data-testid={`photos-invoice-${inv.id}`}>
                          <Camera className="w-3 h-3" /> {(inv.photos || []).length}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-center hidden lg:table-cell">
                        {reconStatus === "critical" && <span className="inline-flex items-center gap-1 text-[10px] text-red-400 bg-red-500/100/10 px-2 py-0.5 rounded-full" data-testid={`recon-${inv.id}`}><ShieldAlert className="w-3 h-3" /> {recon.alerts_count} alerte{recon.alerts_count > 1 ? "s" : ""}</span>}
                        {reconStatus === "warning" && <span className="inline-flex items-center gap-1 text-[10px] text-amber-400 bg-amber-500/100/10 px-2 py-0.5 rounded-full" data-testid={`recon-${inv.id}`}><TriangleAlert className="w-3 h-3" /> {recon.alerts_count}</span>}
                        {reconStatus === "ok" && <span className="inline-flex items-center gap-1 text-[10px] text-emerald-400" data-testid={`recon-${inv.id}`}><ShieldCheck className="w-3 h-3" /></span>}
                        {!reconStatus && <span className="text-[10px] text-slate-600">-</span>}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button onClick={() => setShowStatusUpdate(inv)} className={`px-2 py-0.5 rounded-full border text-[11px] font-medium cursor-pointer hover:opacity-80 ${getStatusStyle(inv.status)}`} data-testid={`invoice-status-${inv.id}`}>
                          {getStatusLabel(inv.status)}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1">
                          <button onClick={() => loadDetail(inv.id)} className="p-1.5 rounded-lg hover:bg-white/5 text-slate-400 hover:text-blue-400" data-testid={`view-invoice-${inv.id}`}><Eye className="w-4 h-4" /></button>
                          <button onClick={() => loadDetail(inv.id)} className="p-1.5 rounded-lg hover:bg-white/5 text-slate-400 hover:text-green-400"><Camera className="w-4 h-4" /></button>
                          <button onClick={() => handleQrDownload(inv.id)} className="p-1.5 rounded-lg hover:bg-white/5 text-slate-400 hover:text-purple-400" title="QR Code" data-testid={`qr-invoice-${inv.id}`}><QrCode className="w-4 h-4" /></button>
                          <button onClick={() => handleDelete(inv.id)} className="p-1.5 rounded-lg hover:bg-white/5 text-slate-400 hover:text-red-400" data-testid={`delete-invoice-${inv.id}`}><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Create Modal */}
        {showCreate && <CreateInvoiceModal insurers={insurers} form={form} setForm={setForm} onSubmit={handleCreate} onClose={() => { setShowCreate(false); resetForm(); }} addAffiliation={addAffiliation} removeAffiliation={removeAffiliation} updateAffiliation={updateAffiliation} />}

        {/* Status Update Modal */}
        {showStatusUpdate && <StatusUpdateModal inv={showStatusUpdate} onClose={() => setShowStatusUpdate(null)} onUpdate={handleStatusUpdate} />}

        {/* Dossier Panel */}
        {showDetail && <InvoiceDetailModal inv={showDetail} initialAction={initialDetailAction} onClose={() => { setShowDetail(null); setInitialDetailAction(null); load(); }} onOpenPhotos={() => {}} onQrDownload={handleQrDownload} insurersList={insurers} />}
      </div>
    </LogLayout>
  );
}

// ---- CREATE INVOICE MODAL ----
function CreateInvoiceModal({ insurers, form, setForm, onSubmit, onClose, addAffiliation, removeAffiliation, updateAffiliation }) {
  const totalFromAff = form.affiliations.reduce((s, a) => s + (parseFloat(a.amount) || 0), 0);
  const totalBons = form.affiliations.reduce((s, a) => s + (parseInt(a.nb_bons) || 0), 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="create-invoice-modal">
      <div className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-white/5">
          <h2 className="text-lg font-bold text-white">Nouvelle facture recapitulative</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={onSubmit} className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400 block mb-1">Assureur *</label>
              <select data-testid="invoice-insurer-select" value={form.insurer_id} onChange={e => { const s = insurers.find(i => i.id === e.target.value); setForm(f => ({ ...f, insurer_id: e.target.value, insurer_name: s?.name || "" })); }} required className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:border-blue-500 focus:outline-none">
                <option value="">Selectionner un assureur</option>
                {insurers.map(ins => <option key={ins.id} value={ins.id}>{ins.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-400 block mb-1">N° Facture *</label>
              <input data-testid="invoice-number-input" value={form.invoice_number} onChange={e => setForm(f => ({ ...f, invoice_number: e.target.value }))} required className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:border-blue-500 focus:outline-none" />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-slate-400 block mb-1">Période debut</label>
              <input type="date" value={form.period_start} onChange={e => setForm(f => ({ ...f, period_start: e.target.value }))} className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:border-blue-500 focus:outline-none" />
            </div>
            <div>
              <label className="text-xs text-slate-400 block mb-1">Période fin</label>
              <input type="date" value={form.period_end} onChange={e => setForm(f => ({ ...f, period_end: e.target.value }))} className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:border-blue-500 focus:outline-none" />
            </div>
            <div>
              <label className="text-xs text-slate-400 block mb-1">Date facture *</label>
              <input data-testid="invoice-date-input" type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} required className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:border-blue-500 focus:outline-none" />
            </div>
          </div>

          {/* Affiliations */}
          <div className="border border-white/10 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-white">Sociétés affiliées</h3>
              <Button type="button" onClick={addAffiliation} size="sm" variant="outline" className="text-blue-400 border-blue-500/30 hover:bg-blue-500/100/10 gap-1 h-7 text-xs" data-testid="add-affiliation-btn">
                <Plus className="w-3 h-3" /> Ajouter
              </Button>
            </div>

            {form.affiliations.length === 0 ? (
              <p className="text-xs text-slate-500 text-center py-3">Aucune affiliation. Cliquez sur "Ajouter" pour ajouter des societes.</p>
            ) : (
              <div className="space-y-2">
                <div className="grid grid-cols-12 gap-2 text-[10px] text-slate-500 uppercase px-1">
                  <span className="col-span-5">Société / Membre</span>
                  <span className="col-span-2 text-center">Nbre Bons</span>
                  <span className="col-span-4 text-right">Montant (FCFA)</span>
                  <span className="col-span-1"></span>
                </div>
                {form.affiliations.map((aff, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`affiliation-row-${idx}`}>
                    <input value={aff.company_name} onChange={e => updateAffiliation(idx, "company_name", e.target.value)} placeholder="Nom société" className="col-span-5 bg-slate-950 border border-white/10 rounded-lg px-2 py-2 text-sm text-white focus:border-blue-500 focus:outline-none" data-testid={`aff-name-${idx}`} />
                    <input type="number" value={aff.nb_bons} onChange={e => updateAffiliation(idx, "nb_bons", e.target.value)} className="col-span-2 bg-slate-950 border border-white/10 rounded-lg px-2 py-2 text-sm text-white text-center focus:border-blue-500 focus:outline-none" data-testid={`aff-bons-${idx}`} />
                    <input type="number" value={aff.amount} onChange={e => updateAffiliation(idx, "amount", e.target.value)} placeholder="0" className="col-span-4 bg-slate-950 border border-white/10 rounded-lg px-2 py-2 text-sm text-white text-right focus:border-blue-500 focus:outline-none" data-testid={`aff-amount-${idx}`} />
                    <button type="button" onClick={() => removeAffiliation(idx)} className="col-span-1 p-1 rounded hover:bg-red-500/100/100/10 text-slate-500 hover:text-red-400"><X className="w-4 h-4" /></button>
                  </div>
                ))}
                {form.affiliations.length > 0 && (
                  <div className="grid grid-cols-12 gap-2 pt-2 border-t border-white/5">
                    <span className="col-span-5 text-xs font-semibold text-white px-1">TOTAL</span>
                    <span className="col-span-2 text-xs font-bold text-blue-400 text-center">{totalBons} bons</span>
                    <span className="col-span-4 text-xs font-bold text-emerald-400 text-right">{formatFCFA(totalFromAff)}</span>
                    <span className="col-span-1"></span>
                  </div>
                )}
              </div>
            )}
          </div>

          {form.affiliations.length === 0 && (
            <div>
              <label className="text-xs text-slate-400 block mb-1">Montant total (FCFA) *</label>
              <input data-testid="invoice-amount-input" type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} required className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:border-blue-500 focus:outline-none" />
            </div>
          )}

          <div>
            <label className="text-xs text-slate-400 block mb-1">Description</label>
            <textarea data-testid="invoice-description-input" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:border-blue-500 focus:outline-none resize-none" />
          </div>
          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1 border-white/10 text-slate-300">Annuler</Button>
            <Button type="submit" data-testid="submit-invoice-btn" className="flex-1 bg-blue-600 hover:bg-blue-500/100 text-white">Creer</Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---- STATUS UPDATE MODAL (WORKFLOW) ----
function StatusUpdateModal({ inv, onClose, onUpdate }) {
  const [rejectMotif, setRejectMotif] = useState("");
  const [showReject, setShowReject] = useState(false);
  const [stampConfirmed, setStampConfirmed] = useState(false);
  const [showDepot, setShowDepot] = useState(false);
  const [depotNote, setDepotNote] = useState("");
  const [showCashPayment, setShowCashPayment] = useState(false);
  const [cashAmount, setCashAmount] = useState("");
  const [cashNote, setCashNote] = useState("");
  const [cashSubmitting, setCashSubmitting] = useState(false);

  const WORKFLOW_STATUSES = [
    { value: "editee", label: "Éditée", color: "bg-blue-500/100/20 text-blue-400 border-blue-500/30" },
    { value: "deposee", label: "Déposée", color: "bg-cyan-100 text-cyan-700 border-cyan-200" },
    { value: "rejetee", label: "Rejetée", color: "bg-red-100 text-red-700 border-red-200" },
    { value: "paiement_partiel", label: "Paiement partiel", color: "bg-amber-100 text-amber-700 border-amber-200" },
    { value: "paiement_effectue", label: "Paiement effectué", color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
    { value: "archived", label: "Archivée", color: "bg-slate-100 text-slate-600 border-slate-200" },
  ];

  const handleDepotConfirm = async () => {
    try {
      await axios.put(`${API}/log/invoices/${inv.id}/confirm-depot`, { stamp_confirmed: stampConfirmed, note: depotNote }, { headers: getHeaders() });
      toast.success("Dépôt confirmé avec succès");
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur confirmation dépôt"); }
  };

  const handleReject = async () => {
    if (!rejectMotif.trim()) return toast.error("Le motif de rejet est obligatoire");
    try {
      await axios.put(`${API}/log/invoices/${inv.id}/reject`, { motif: rejectMotif }, { headers: getHeaders() });
      toast.success("Facture rejetée");
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur rejet"); }
  };

  const handleCashPayment = async () => {
    const amount = parseFloat(cashAmount);
    if (!amount || amount <= 0) return toast.error("Montant invalide");
    setCashSubmitting(true);
    try {
      await axios.post(`${API}/log/invoices/${inv.id}/cash-payment`, {
        amount,
        note: cashNote || "Paiement en espèces",
      }, { headers: getHeaders() });
      toast.success(`Paiement espèces de ${new Intl.NumberFormat("fr-FR").format(amount)} FCFA enregistré`);
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur paiement"); }
    finally { setCashSubmitting(false); }
  };

  const currentStatus = inv.status;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="status-update-modal">
      <div className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b border-white/5">
          <h2 className="text-base font-bold text-white">Workflow Facture</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-xs text-slate-400 mb-1">Facture: <span className="text-white font-medium">{inv.invoice_number}</span> — {inv.insurer_name}</p>
          <p className="text-xs text-slate-500">Statut actuel: <span className={`px-2 py-0.5 rounded-full border text-[11px] font-medium ${getStatusStyle(currentStatus)}`}>{getStatusLabel(currentStatus)}</span></p>

          {/* Workflow Actions */}
          <div className="border-t border-white/5 pt-3 space-y-2">
            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Actions du workflow</p>

            {/* Confirm Dépôt */}
            {(currentStatus === "editee" || currentStatus === "received") && !showDepot && !showReject && (
              <button onClick={() => setShowDepot(true)} data-testid="action-confirm-dépôt"
                className="w-full text-left px-4 py-3 rounded-lg border border-cyan-500/20 text-cyan-400 hover:bg-cyan-500/100/100/10 text-sm flex items-center gap-2">
                <Scan className="w-4 h-4" /> Confirmer le dépôt (cachet assureur)
              </button>
            )}

            {showDepot && (
              <div className="bg-cyan-500/100/5 border border-cyan-500/20 rounded-lg p-3 space-y-2">
                <label className="flex items-center gap-2 text-sm text-white cursor-pointer">
                  <input type="checkbox" checked={stampConfirmed} onChange={e => setStampConfirmed(e.target.checked)} className="rounded border-white/20 bg-slate-800" />
                  Je confirme la présence du cachet de l'assureur
                </label>
                <input value={depotNote} onChange={e => setDepotNote(e.target.value)} placeholder="Note (optionnel)" className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" />
                <div className="flex gap-2">
                  <Button onClick={handleDepotConfirm} disabled={!stampConfirmed} className="bg-cyan-600 hover:bg-cyan-500/100/100 text-white text-sm flex-1" data-testid="confirm-dépôt-btn">
                    <CheckCircle className="w-4 h-4 mr-1" /> Confirmer dépôt
                  </Button>
                  <Button onClick={() => setShowDepot(false)} variant="outline" className="border-white/10 text-slate-400 text-sm">Annuler</Button>
                </div>
              </div>
            )}

            {/* Reject */}
            {!showReject && !showDepot && (
              <button onClick={() => setShowReject(true)} data-testid="action-reject"
                className="w-full text-left px-4 py-3 rounded-lg border border-red-500/20 text-red-400 hover:bg-red-500/100/100/10 text-sm flex items-center gap-2">
                <AlertCircle className="w-4 h-4" /> Rejeter la facture
              </button>
            )}

            {showReject && (
              <div className="bg-red-500/100/5 border border-red-500/20 rounded-lg p-3 space-y-2">
                <input value={rejectMotif} onChange={e => setRejectMotif(e.target.value)} placeholder="Motif de rejet (obligatoire)" data-testid="reject-motif-input"
                  className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" />
                <div className="flex gap-2">
                  <Button onClick={handleReject} disabled={!rejectMotif.trim()} className="bg-red-600 hover:bg-red-500/100/100 text-white text-sm flex-1" data-testid="confirm-reject-btn">Confirmer le rejet</Button>
                  <Button onClick={() => setShowReject(false)} variant="outline" className="border-white/10 text-slate-400 text-sm">Annuler</Button>
                </div>
              </div>
            )}

            {/* Cash Payment */}
            {!showDepot && !showReject && !showCashPayment && !["paiement_effectue", "archived"].includes(currentStatus) && (
              <button onClick={() => { setShowCashPayment(true); setCashAmount(String(inv.remaining_amount || inv.amount || "")); }} data-testid="action-cash-payment"
                className="w-full text-left px-4 py-3 rounded-lg border border-emerald-500/20 text-emerald-400 hover:bg-emerald-500/10 text-sm flex items-center gap-2">
                <CreditCard className="w-4 h-4" /> Enregistrer un paiement en espèces
              </button>
            )}

            {showCashPayment && (
              <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-3 space-y-2">
                <p className="text-xs text-slate-400">Montant restant: <span className="text-white font-medium">{formatFCFA(inv.remaining_amount || inv.amount)}</span></p>
                <input type="number" value={cashAmount} onChange={e => setCashAmount(e.target.value)} placeholder="Montant (FCFA)" data-testid="cash-amount-input"
                  className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" />
                <input value={cashNote} onChange={e => setCashNote(e.target.value)} placeholder="Note (ex: Reçu espèces N123)" data-testid="cash-note-input"
                  className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" />
                <div className="flex gap-2">
                  <Button onClick={handleCashPayment} disabled={cashSubmitting || !cashAmount} className="bg-emerald-600 hover:bg-emerald-500 text-white text-sm flex-1" data-testid="confirm-cash-btn">
                    {cashSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle className="w-4 h-4 mr-1" />} Confirmer paiement espèces
                  </Button>
                  <Button onClick={() => setShowCashPayment(false)} variant="outline" className="border-white/10 text-slate-400 text-sm">Annuler</Button>
                </div>
              </div>
            )}
          </div>

          {/* Manual status override (for admin) */}
          <div className="border-t border-white/5 pt-3">
            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Changement manuel</p>
            <div className="grid grid-cols-2 gap-1.5">
              {WORKFLOW_STATUSES.map(s => (
                <button key={s.value} onClick={() => onUpdate(inv.id, s.value)} data-testid={`set-status-${s.value}`}
                  className={`text-left px-3 py-2 rounded-lg border text-xs transition-all ${currentStatus === s.value ? s.color + " font-semibold" : "border-white/5 text-slate-400 hover:bg-white/5"}`}>
                  {s.label} {currentStatus === s.value && " (actuel)"}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---- DOSSIER PANEL (replaces old detail modal) ----
const TABS = [
  { id: "documents", label: "Documents", icon: Upload },
  { id: "info", label: "Informations", icon: FileText },
  { id: "paiements", label: "Paiements", icon: CreditCard },
  { id: "correction", label: "Corriger OCR", icon: Scan },
  { id: "reconciliation", label: "Contrôle", icon: ShieldCheck },
  { id: "history", label: "Historique", icon: FileText },
];

function InvoiceDetailModal({ inv, initialAction, onClose, onOpenPhotos, onQrDownload, insurersList }) {
  const [tab, setTab] = useState("documents");
  const [invoice, setInvoice] = useState(inv);
  const [recon, setRecon] = useState(inv.reconciliation || null);
  const [reconLoading, setReconLoading] = useState(false);
  const registeredInsurers = insurersList || [];

  const hasOcrData = invoice.ocr_total_amount || invoice.ocr_affiliations || invoice.ocr_insurer_name || invoice.ocr_check_amount || invoice.ocr_deposit_amount;

  const refreshInvoice = async () => {
    try {
      const res = await axios.get(`${API}/log/invoices/${inv.id}`, { headers: getHeaders() });
      setInvoice(res.data.invoice);
      setRecon(res.data.invoice.reconciliation || null);
    } catch (e) { /* ignore */ }
  };

  const [emailAlert, setEmailAlert] = useState(null);

  const runReconciliation = async () => {
    setReconLoading(true);
    try {
      const res = await axios.post(`${API}/log/invoices/${inv.id}/reconciliation`, {}, { headers: getHeaders() });
      setRecon(res.data.reconciliation);
      if (res.data.email_alert) {
        setEmailAlert(res.data.email_alert);
        toast.success("Vérification effectuée + alerte email envoyee");
      } else {
        toast.success("Vérification croisee effectuée");
      }
    } catch (e) { toast.error("Erreur"); }
    finally { setReconLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-2 md:p-4" data-testid="invoice-detail-modal">
      <div className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-4xl max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/5 shrink-0">
          <div>
            <h2 className="text-lg font-bold text-white">Dossier {invoice.invoice_number}</h2>
            <div className="flex items-center gap-3 mt-1">
              <span className="text-sm text-slate-400">{invoice.insurer_name}</span>
              <span className={`px-2 py-0.5 rounded-full border text-xs font-medium ${getStatusStyle(invoice.status)}`}>{getStatusLabel(invoice.status)}</span>
              {recon?.status === "critical" && <span className="inline-flex items-center gap-1 text-xs text-red-400"><ShieldAlert className="w-3 h-3" /> Ecarts</span>}
              {recon?.status === "ok" && <span className="inline-flex items-center gap-1 text-xs text-emerald-400"><ShieldCheck className="w-3 h-3" /> Conforme</span>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => onQrDownload(inv.id)} className="p-2 rounded-lg hover:bg-white/5 text-slate-400 hover:text-purple-400" title="QR Code" data-testid="detail-qr-btn"><QrCode className="w-5 h-5" /></button>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/5 text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
          </div>
        </div>

        {/* KPIs bar */}
        <div className="grid grid-cols-4 gap-3 px-5 py-3 border-b border-white/5 shrink-0">
          <div className="text-center"><p className="text-xs text-slate-500">Montant</p><p className="text-sm font-bold text-white">{formatFCFA(invoice.amount)}</p></div>
          <div className="text-center"><p className="text-xs text-slate-500">Payé</p><p className="text-sm font-bold text-emerald-400">{formatFCFA(invoice.paid_amount)}</p></div>
          <div className="text-center"><p className="text-xs text-slate-500">Restant</p><p className="text-sm font-bold text-amber-400">{formatFCFA(invoice.remaining_amount)}</p></div>
          <div className="text-center"><p className="text-xs text-slate-500">Documents</p><p className="text-sm font-bold text-blue-400">{(invoice.photos || []).length}</p></div>
        </div>

        {/* Reconciliation suggestions (only for cheque/remise dossiers) */}
        {(invoice.doc_type === "cheque" || invoice.doc_type === "remise") && (
          <ReconciliationBanner invoice={invoice} onReconciled={refreshInvoice} onJumpTo={onClose} />
        )}

        {/* Inverse reconciliation : facture unpaid → find matching cheque */}
        {invoice.doc_type !== "cheque" && invoice.doc_type !== "remise" && !invoice.reconciled_with && (invoice.remaining_amount > 0) && (
          <InverseReconciliationBanner invoice={invoice} onReconciled={refreshInvoice} />
        )}

        {/* Tabs */}
        <div className="flex border-b border-white/5 px-5 shrink-0">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${tab === t.id ? "border-blue-500 text-blue-400" : "border-transparent text-slate-500 hover:text-slate-300"}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {/* ---- Documents Tab ---- */}
          {tab === "documents" && (
            <DossierDocumentsTab invoice={invoice} onRefresh={refreshInvoice} initialAction={initialAction} />
          )}

          {/* ---- Info Tab ---- */}
          {tab === "info" && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                <InfoField label="N° Facture" value={invoice.invoice_number} />
                <InfoField label="Assureur" value={invoice.insurer_name} />
                <InfoField label="Montant total" value={formatFCFA(invoice.amount)} accent="white" />
                {invoice.period_start && <InfoField label="Période" value={`${invoice.period_start} → ${invoice.period_end}`} />}
                {invoice.date && <InfoField label="Date facture" value={invoice.date} />}
                {invoice.description && <InfoField label="Description" value={invoice.description} />}
              </div>

              {hasOcrData && (
                <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="w-4 h-4 text-emerald-400" />
                    <h3 className="text-sm font-semibold text-emerald-400">Données extraites par OCR</h3>
                  </div>
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                    {invoice.ocr_insurer_name && <InfoField label="Assureur (OCR)" value={invoice.ocr_insurer_name} />}
                    {invoice.ocr_total_amount && <InfoField label="Montant (OCR)" value={formatFCFA(invoice.ocr_total_amount)} accent="emerald" />}
                    {invoice.ocr_date && <InfoField label="Date (OCR)" value={invoice.ocr_date} />}
                    {invoice.ocr_check_amount && <InfoField label="Chèque (OCR)" value={formatFCFA(invoice.ocr_check_amount)} accent="emerald" />}
                    {invoice.ocr_check_bank && <InfoField label="Banque chèque" value={invoice.ocr_check_bank} />}
                    {invoice.ocr_deposit_amount && <InfoField label="Remise (OCR)" value={formatFCFA(invoice.ocr_deposit_amount)} accent="emerald" />}
                  </div>
                  {invoice.ocr_affiliations?.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs text-slate-500 uppercase mb-2">Affiliations OCR</p>
                      {invoice.ocr_affiliations.map((a, i) => (
                        <div key={i} className="flex justify-between bg-slate-900 rounded px-3 py-1.5 text-sm mb-1">
                          <span className="text-white">{a.company_name || "?"}</span>
                          <span className="text-slate-400">{a.nb_bons || 0} bons</span>
                          <span className="text-emerald-400">{formatFCFA(a.amount || 0)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {(invoice.affiliations || []).length > 0 && (
                <div>
                  <p className="text-sm text-slate-400 font-semibold mb-2 uppercase">Sociétés affiliées ({invoice.affiliations.length})</p>
                  <div className="bg-slate-950 rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead><tr className="text-xs text-slate-500 uppercase border-b border-white/5">
                        <th className="text-left px-4 py-2.5">Société / Membre</th>
                        <th className="text-center px-4 py-2.5">Bons</th>
                        <th className="text-right px-4 py-2.5">Montant</th>
                      </tr></thead>
                      <tbody>
                        {invoice.affiliations.map((a, i) => (
                          <tr key={i} className="border-b border-white/5">
                            <td className="px-4 py-2.5 text-white">{a.company_name}</td>
                            <td className="px-4 py-2.5 text-center text-slate-300">{a.nb_bons}</td>
                            <td className="px-4 py-2.5 text-right text-emerald-400 font-medium">{formatFCFA(a.amount)}</td>
                          </tr>
                        ))}
                        <tr className="bg-slate-900">
                          <td className="px-4 py-2.5 font-bold text-white">TOTAL</td>
                          <td className="px-4 py-2.5 text-center font-bold text-blue-400">{invoice.total_bons || invoice.affiliations.reduce((s, a) => s + (a.nb_bons || 0), 0)}</td>
                          <td className="px-4 py-2.5 text-right font-bold text-emerald-400">{formatFCFA(invoice.amount)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ---- Paiements Tab ---- */}
          {tab === "paiements" && (
            <PaiementsTab invoice={invoice} onRefresh={refreshInvoice} />
          )}

          {/* ---- Correction OCR Tab ---- */}
          {tab === "correction" && (
            <OcrCorrectionTab invoice={invoice} onSave={refreshInvoice} insurers={registeredInsurers} />
          )}

          {/* ---- Reconciliation Tab ---- */}
          {tab === "reconciliation" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-white">Vérification croisee des montants</h3>
                <Button size="sm" onClick={runReconciliation} disabled={reconLoading} className="bg-blue-600 hover:bg-blue-500/100 text-white gap-2" data-testid="run-reconciliation-btn">
                  {reconLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Scan className="w-4 h-4" />} Verifier
                </Button>
              </div>
              {!recon ? (
                <div className="bg-slate-950 rounded-xl p-8 text-center border border-white/5">
                  <ShieldCheck className="w-10 h-10 text-slate-600 mx-auto mb-3" />
                  <p className="text-sm text-slate-400">Cliquez sur "Verifier" pour lancer la vérification croisee.</p>
                  <p className="text-xs text-slate-500 mt-1">Compare les montants de la facture, du chèque et de la remise.</p>
                </div>
              ) : recon.status === "ok" ? (
                <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-6 text-center">
                  <ShieldCheck className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
                  <p className="text-base text-emerald-400 font-semibold">Aucun ecart détecté</p>
                  <p className="text-sm text-slate-400 mt-1">Les montants sont cohérents entre tous les documents.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {recon.alerts.map((alert, i) => (
                    <div key={i} className={`rounded-xl p-4 border ${alert.level === "critical" ? "bg-red-500/100/5 border-red-500/20" : "bg-amber-500/100/5 border-amber-500/20"}`} data-testid={`alert-${i}`}>
                      <div className="flex items-start gap-3">
                        {alert.level === "critical" ? <ShieldAlert className="w-5 h-5 text-red-400 mt-0.5 shrink-0" /> : <TriangleAlert className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />}
                        <div>
                          <p className={`text-sm font-semibold ${alert.level === "critical" ? "text-red-400" : "text-amber-400"}`}>{alert.title}</p>
                          <p className="text-sm text-slate-300 mt-1">{alert.message}</p>
                          {alert.level === "critical" && <p className="text-xs text-red-300 mt-2 font-medium bg-red-500/100/10 rounded px-2 py-1 inline-block">Action requise: Verifiez les documents ou contactez l'admin.</p>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {recon?.amounts && (
                <div className="bg-slate-950 rounded-xl p-5 border border-white/5">
                  <p className="text-xs text-slate-500 uppercase mb-3 font-semibold">Résumé des montants</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div><p className="text-xs text-slate-500">Facture (saisi)</p><p className="text-base font-bold text-white">{formatFCFA(recon.amounts.invoice_manual)}</p></div>
                    {recon.amounts.ocr_facture != null && <div><p className="text-xs text-slate-500">Facture (OCR)</p><p className="text-base font-bold text-white">{formatFCFA(recon.amounts.ocr_facture)}</p></div>}
                    {recon.amounts.ocr_cheque != null && <div><p className="text-xs text-slate-500">Chèque (OCR)</p><p className="text-base font-bold text-white">{formatFCFA(recon.amounts.ocr_cheque)}</p></div>}
                    {recon.amounts.ocr_remise != null && <div><p className="text-xs text-slate-500">Remise (OCR)</p><p className="text-base font-bold text-white">{formatFCFA(recon.amounts.ocr_remise)}</p></div>}
                  </div>
                </div>
              )}
              {emailAlert && (
                <div className="bg-blue-500/100/5 border border-blue-500/20 rounded-xl p-4 flex items-start gap-3" data-testid="email-alert-sent">
                  <div className="w-8 h-8 rounded-lg bg-blue-500/100/10 flex items-center justify-center shrink-0">
                    <FileText className="w-4 h-4 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-blue-400">Alerte email {emailAlert.status === "mock_sent" ? "simulée" : "envoyée"}</p>
                    <p className="text-xs text-slate-400 mt-0.5">Destinataires: {(emailAlert.recipients || []).join(", ")}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{emailAlert.subject}</p>
                    {emailAlert.status === "sent" && (
                      <p className="text-xs text-emerald-400 mt-1">Email envoyé avec succès via Resend</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ---- History Tab ---- */}
          {tab === "history" && (
            <div className="space-y-3">
              {(invoice.status_history || []).length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-8">Aucun historique</p>
              ) : (
                invoice.status_history.map((h, i) => (
                  <div key={i} className="flex items-start gap-4 p-3 bg-slate-950 rounded-xl border border-white/5">
                    <div className={`w-3 h-3 rounded-full mt-1 shrink-0 ${getStatusStyle(h.status).split(" ")[0]}`} />
                    <div>
                      <p className="text-sm text-white font-medium">{getStatusLabel(h.status)}</p>
                      {h.note && <p className="text-sm text-slate-400 mt-0.5">{h.note}</p>}
                      <p className="text-xs text-slate-500 mt-1">{new Date(h.date).toLocaleString("fr-FR")} - {h.by}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function InfoField({ label, value, accent = "default" }) {
  const textClass = accent === "emerald" ? "text-emerald-400 font-medium" : accent === "white" ? "text-white font-bold" : "text-white";
  return (
    <div>
      <p className="text-xs text-slate-500 uppercase mb-0.5">{label}</p>
      <p className={`text-sm ${textClass}`}>{value}</p>
    </div>
  );
}

// ---- PAIEMENTS TAB (cash/cheque/transfer payment + payment history) ----
function PaiementsTab({ invoice, onRefresh }) {
  const [method, setMethod] = useState("especes");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  // Cheque-specific
  const [chequeNumber, setChequeNumber] = useState("");
  const [bankName, setBankName] = useState("");
  const [expectedDate, setExpectedDate] = useState("");
  // Transfer-specific
  const [transferRef, setTransferRef] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const resetFields = () => {
    setAmount(""); setNote(""); setChequeNumber(""); setBankName(""); setExpectedDate(""); setTransferRef("");
  };

  const handlePayment = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return toast.error("Montant invalide");
    if (method === "cheque" && !chequeNumber.trim()) return toast.error("Numéro de chèque requis");
    setSubmitting(true);
    try {
      let endpoint, payload, label;
      if (method === "especes") {
        endpoint = "cash-payment";
        payload = { amount: amt, note: note || "Paiement en espèces" };
        label = "Paiement espèces";
      } else if (method === "cheque") {
        endpoint = "cheque-payment";
        payload = {
          amount: amt,
          cheque_number: chequeNumber.trim(),
          bank_name: bankName.trim(),
          expected_date: expectedDate || "",
          note: note || "",
        };
        label = `Chèque n°${chequeNumber}`;
      } else {
        endpoint = "transfer-payment";
        payload = {
          amount: amt,
          reference: transferRef.trim(),
          bank_name: bankName.trim(),
          expected_date: expectedDate || "",
          note: note || "",
        };
        label = "Virement";
      }
      const res = await axios.post(`${API}/log/invoices/${invoice.id}/${endpoint}`, payload, { headers: getHeaders() });
      toast.success(res.data.message || `${label} de ${new Intl.NumberFormat("fr-FR").format(amt)} FCFA enregistré`);
      resetFields();
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur paiement"); }
    finally { setSubmitting(false); }
  };

  const isFullyPaid = ["paiement_effectue", "archived"].includes(invoice.status);
  const paymentHistory = (invoice.status_history || []).filter(h => h.payment_amount || h.payment_method);

  const METHODS = [
    { id: "especes", label: "Espèces", icon: CreditCard, color: "emerald", desc: "Encaissé immédiatement" },
    { id: "cheque", label: "Chèque", icon: FileText, color: "sky", desc: "→ Pipeline : Remise de chèque" },
    { id: "virement", label: "Virement", icon: ArrowRight, color: "violet", desc: "→ Pipeline : Bordereau à récupérer" },
  ];
  const TONE = {
    emerald: { ring: "border-emerald-500/40", soft: "bg-emerald-500/10", text: "text-emerald-400", btn: "bg-emerald-600 hover:bg-emerald-500" },
    sky: { ring: "border-sky-500/40", soft: "bg-sky-500/10", text: "text-sky-400", btn: "bg-sky-600 hover:bg-sky-500" },
    violet: { ring: "border-violet-500/40", soft: "bg-violet-500/10", text: "text-violet-400", btn: "bg-violet-600 hover:bg-violet-500" },
  };
  const currentTone = TONE[METHODS.find(m => m.id === method).color];

  return (
    <div className="space-y-5" data-testid="paiements-tab">
      {/* KPIs */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-slate-950 border border-white/5 rounded-xl p-3 text-center">
          <p className="text-xs text-slate-500">Montant total</p>
          <p className="text-lg font-bold text-white">{formatFCFA(invoice.amount)}</p>
        </div>
        <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-3 text-center">
          <p className="text-xs text-emerald-400">Total payé</p>
          <p className="text-lg font-bold text-emerald-400">{formatFCFA(invoice.paid_amount || 0)}</p>
        </div>
        <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-3 text-center">
          <p className="text-xs text-amber-400">Reste à payer</p>
          <p className="text-lg font-bold text-amber-400">{formatFCFA(invoice.remaining_amount ?? (invoice.amount - (invoice.paid_amount || 0)))}</p>
        </div>
      </div>

      {/* Enregistrer un paiement */}
      {!isFullyPaid && (
        <div className={`bg-slate-950 border ${currentTone.ring} rounded-xl p-4 space-y-3`} data-testid="payment-card">
          <h3 className={`text-sm font-semibold ${currentTone.text} flex items-center gap-2`}>
            <CreditCard className="w-4 h-4" /> Enregistrer un paiement
          </h3>

          {/* Method tabs */}
          <div className="grid grid-cols-3 gap-2">
            {METHODS.map((m) => {
              const Icon = m.icon;
              const t = TONE[m.color];
              const active = method === m.id;
              return (
                <button key={m.id} onClick={() => setMethod(m.id)} data-testid={`method-${m.id}`}
                  className={`p-3 rounded-lg border text-left transition-all ${active ? `${t.ring} ${t.soft}` : "border-white/5 bg-white/2 hover:bg-white/5"}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <Icon className={`w-4 h-4 ${active ? t.text : "text-slate-400"}`} />
                    <p className={`text-xs font-semibold ${active ? t.text : "text-slate-300"}`}>{m.label}</p>
                  </div>
                  <p className="text-[10px] text-slate-500 leading-tight">{m.desc}</p>
                </button>
              );
            })}
          </div>

          {/* Common: amount + note */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400 block mb-1">Montant (FCFA) *</label>
              <input type="number" value={amount} onChange={e => setAmount(e.target.value)}
                placeholder={`Max: ${new Intl.NumberFormat("fr-FR").format(invoice.remaining_amount || invoice.amount)}`}
                className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                data-testid="detail-cash-amount-input" />
            </div>
            <div>
              <label className="text-xs text-slate-400 block mb-1">Note (optionnel)</label>
              <input value={note} onChange={e => setNote(e.target.value)}
                placeholder="Référence interne, contexte…"
                className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                data-testid="detail-cash-note-input" />
            </div>
          </div>

          {/* Cheque-specific */}
          {method === "cheque" && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 bg-sky-500/5 border border-sky-500/20 rounded-lg p-3">
              <div>
                <label className="text-xs text-sky-300 block mb-1">N° de chèque *</label>
                <input value={chequeNumber} onChange={e => setChequeNumber(e.target.value)}
                  placeholder="Ex: 1234567"
                  className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                  data-testid="cheque-number-input" />
              </div>
              <div>
                <label className="text-xs text-sky-300 block mb-1">Banque</label>
                <input value={bankName} onChange={e => setBankName(e.target.value)}
                  placeholder="Ex: SGCI, NSIA Banque…"
                  className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                  data-testid="cheque-bank-input" />
              </div>
              <div>
                <label className="text-xs text-sky-300 block mb-1">Date d'encaissement prévue</label>
                <input type="date" value={expectedDate} onChange={e => setExpectedDate(e.target.value)}
                  className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                  data-testid="cheque-expected-date" />
              </div>
            </div>
          )}

          {/* Transfer-specific */}
          {method === "virement" && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 bg-violet-500/5 border border-violet-500/20 rounded-lg p-3">
              <div>
                <label className="text-xs text-violet-300 block mb-1">Référence virement</label>
                <input value={transferRef} onChange={e => setTransferRef(e.target.value)}
                  placeholder="Ex: VIR-NSIA-2026-0042"
                  className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                  data-testid="transfer-ref-input" />
              </div>
              <div>
                <label className="text-xs text-violet-300 block mb-1">Banque émettrice</label>
                <input value={bankName} onChange={e => setBankName(e.target.value)}
                  placeholder="Ex: SGCI, BNI…"
                  className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                  data-testid="transfer-bank-input" />
              </div>
              <div>
                <label className="text-xs text-violet-300 block mb-1">Date du virement</label>
                <input type="date" value={expectedDate} onChange={e => setExpectedDate(e.target.value)}
                  className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                  data-testid="transfer-expected-date" />
              </div>
            </div>
          )}

          <Button onClick={handlePayment} disabled={submitting || !amount}
            className={`${currentTone.btn} text-white text-sm`} data-testid="detail-confirm-cash-btn">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle className="w-4 h-4 mr-1" />}
            Confirmer le paiement
          </Button>
        </div>
      )}

      {isFullyPaid && (
        <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4 text-center">
          <CheckCircle className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
          <p className="text-sm font-semibold text-emerald-400">Facture entièrement payée</p>
        </div>
      )}

      {/* Historique des paiements */}
      <div>
        <h3 className="text-sm font-semibold text-slate-400 uppercase mb-3">Historique des paiements</h3>
        {paymentHistory.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-6">Aucun paiement enregistré</p>
        ) : (
          <div className="space-y-2">
            {paymentHistory.map((h, i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-slate-950 border border-white/5 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                    <CreditCard className="w-4 h-4 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-sm text-white font-medium">
                      {h.payment_method === "especes" ? "Espèces" : h.payment_method === "cheque" ? "Chèque" : h.payment_method || "Paiement"}
                    </p>
                    {h.note && <p className="text-xs text-slate-500">{h.note}</p>}
                    <p className="text-[10px] text-slate-600">{new Date(h.date).toLocaleString("fr-FR")} — {h.by}</p>
                  </div>
                </div>
                <p className="text-sm font-bold text-emerald-400">+{formatFCFA(h.payment_amount || 0)}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


// ---- DOCUMENTS TAB (embedded photo upload + OCR) ----
function DossierDocumentsTab({ invoice, onRefresh, initialAction }) {
  const [uploading, setUploading] = useState(false);
  const [category, setCategory] = useState("facture");
  const [photos, setPhotos] = useState(invoice.photos || []);
  const [photoUrls, setPhotoUrls] = useState({});
  const [showOcrData, setShowOcrData] = useState(null);
  const [viewFullImg, setViewFullImg] = useState(null);
  const [showArchiveImport, setShowArchiveImport] = useState(initialAction === "archives");
  const fileRef = useRef(null);

  useEffect(() => { setPhotos(invoice.photos || []); }, [invoice.photos]);

  useEffect(() => {
    photos.forEach(async (p) => {
      if (photoUrls[p.id]) return;
      try {
        const res = await axios.get(`${API}/log/invoices/${invoice.id}/photos/${p.id}`, { headers: getHeaders(), responseType: "blob" });
        setPhotoUrls(prev => ({ ...prev, [p.id]: URL.createObjectURL(res.data) }));
      } catch (e) { /* ignore */ }
    });
  }, [photos]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll OCR status
  useEffect(() => {
    const pending = photos.filter(p => p.ocr_status === "pending" || p.ocr_status === "processing");
    if (pending.length === 0) return;
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/log/invoices/${invoice.id}`, { headers: getHeaders() });
        const updated = res.data.invoice?.photos || [];
        // Detect new ownership warnings (not present in previous state)
        const prevWarnIds = new Set(photos.filter(p => p.ownership_warning).map(p => p.id));
        const newWarns = updated.filter(p => p.ownership_warning && !prevWarnIds.has(p.id));
        if (newWarns.length > 0) {
          newWarns.forEach(p => {
            const sev = p.ownership_severity === "high" ? "error" : "warning";
            toast[sev](`⚠️ Document non concordant : ${p.ownership_message || "ce document n'appartient pas à votre pharmacie"}`, { duration: 8000 });
          });
        }
        setPhotos(updated);
        if (!updated.some(p => p.ocr_status === "pending" || p.ocr_status === "processing")) {
          clearInterval(interval);
          onRefresh();
        }
      } catch (e) { /* ignore */ }
    }, 3000);
    return () => clearInterval(interval);
  }, [photos, invoice.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", category);
      const res = await axios.post(`${API}/log/invoices/${invoice.id}/photos`, fd, { headers: { ...getHeaders(), "Content-Type": "multipart/form-data" } });
      const statusChanged = res.data.status_changed;
      if (statusChanged === "deposee") {
        toast.success("Facture cachetée enregistrée — Statut : Déposée ✓");
      } else if (statusChanged) {
        toast.success(`Document ajouté — Statut : ${statusChanged}`);
      } else {
        toast.success("Document ajouté - OCR en cours...");
      }
      setPhotos(prev => [...prev, res.data.photo]);
      // Refresh parent to reflect new invoice status (deposee, paiement_*, ...)
      if (statusChanged) onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur upload"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const handleDelete = async (photoId) => {
    if (!window.confirm("Supprimer ce document ?")) return;
    try {
      await axios.delete(`${API}/log/invoices/${invoice.id}/photos/${photoId}`, { headers: getHeaders() });
      setPhotos(prev => prev.filter(p => p.id !== photoId));
      toast.success("Document supprimé");
    } catch (e) { toast.error("Erreur"); }
  };

  const triggerOcr = async (photoId) => {
    try {
      await axios.post(`${API}/log/invoices/${invoice.id}/photos/${photoId}/ocr`, {}, { headers: getHeaders() });
      toast.success("OCR relance");
      setPhotos(prev => prev.map(p => p.id === photoId ? { ...p, ocr_status: "pending" } : p));
    } catch (e) { toast.error("Erreur OCR"); }
  };

  const applyOcr = async () => {
    try {
      const res = await axios.post(`${API}/log/invoices/${invoice.id}/apply-ocr`, {}, { headers: getHeaders() });
      toast.success(`Données OCR appliquees (${res.data.applied_fields?.length || 0} champs)`);
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const hasCompletedOcr = photos.some(p => p.ocr_status === "completed");
  const ownershipWarnings = photos.filter(p => p.ownership_warning);
  const hasHighSeverityWarning = photos.some(p => p.ownership_warning && p.ownership_severity === "high");
  const grouped = PHOTO_CATEGORIES.map(cat => ({ ...cat, photos: photos.filter(p => p.category === cat.value) }));

  return (
    <div className="space-y-5">
      {/* OWNERSHIP WARNING BANNER */}
      {ownershipWarnings.length > 0 && (
        <div className="bg-red-500/10 border-2 border-red-500/40 rounded-xl p-4" data-testid="ownership-warning-banner">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-6 h-6 text-red-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="text-sm font-bold text-red-300 mb-1">
                ⚠️ {ownershipWarnings.length} document{ownershipWarnings.length > 1 ? "s" : ""} non concordant{ownershipWarnings.length > 1 ? "s" : ""} détecté{ownershipWarnings.length > 1 ? "s" : ""}
              </h4>
              <p className="text-xs text-red-200/90 mb-2">
                L'OCR a détecté que certains documents scannés ne semblent pas appartenir à votre pharmacie. Vérifiez avant d'appliquer les données.
              </p>
              <ul className="space-y-1.5">
                {ownershipWarnings.map(p => (
                  <li key={p.id} className="text-xs bg-red-950/30 rounded px-2 py-1.5 border border-red-500/20" data-testid={`ownership-warning-${p.id}`}>
                    <span className="font-semibold text-red-300">{p.original_filename}</span>
                    <span className="text-red-200/70 ml-2">({p.category})</span>
                    {p.ownership_message && <p className="text-[11px] text-red-200/80 mt-0.5">{p.ownership_message}</p>}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
      {/* Upload area */}
      <div className="bg-slate-950 rounded-xl p-5 border-2 border-dashed border-white/10 hover:border-blue-500/30 transition-colors">
        <div className="text-center mb-4">
          <Upload className="w-8 h-8 text-blue-400 mx-auto mb-2" />
          <p className="text-sm font-medium text-white">Ajouter un document</p>
          <p className="text-xs text-slate-500 mt-1">L'OCR extraira automatiquement les informations</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
          {PHOTO_CATEGORIES.map(cat => (
            <button key={cat.value} onClick={() => setCategory(cat.value)} data-testid={`photo-cat-${cat.value}`}
              className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${category === cat.value ? "bg-blue-600 text-white ring-2 ring-blue-300" : "bg-slate-900 text-slate-400 hover:bg-slate-800"}`}>
              <cat.icon className="w-4 h-4" /> {cat.label}
            </button>
          ))}
        </div>
        <p className="text-center text-xs text-blue-300 mb-3" data-testid="selected-category-hint">
          Catégorie sélectionnée : <span className="font-bold">{PHOTO_CATEGORIES.find(c => c.value === category)?.label}</span>
          {category === "depot" && <span className="text-emerald-400 ml-2">→ statut passera automatiquement à "Déposée"</span>}
        </p>
        <input ref={fileRef} type="file" accept="image/*,.pdf,.xls,.xlsx,.xlsm" onChange={handleUpload} className="hidden" data-testid="photo-file-input" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="w-full bg-blue-600 hover:bg-blue-500/100 text-white gap-2 py-3" data-testid="upload-photo-btn">
            {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Upload en cours...</> : <><Camera className="w-4 h-4" /> Téléverser un document</>}
          </Button>
          <Button onClick={() => setShowArchiveImport(true)} disabled={uploading} variant="outline" className="w-full border-blue-500/40 text-blue-300 hover:bg-blue-500/10 gap-2 py-3" data-testid="import-archive-btn">
            <FolderOpen className="w-4 h-4" /> Importer depuis Archives
          </Button>
        </div>
        <p className="text-center text-[10px] text-slate-500 mt-2">Image / PDF / Excel — l'extraction OCR s'applique aux 3 formats</p>
      </div>

      {/* Apply OCR button */}
      {hasCompletedOcr && (
        <Button
          onClick={() => {
            if (hasHighSeverityWarning) {
              if (!window.confirm("⚠️ ATTENTION : au moins un document a été détecté comme NE PAS appartenir à votre pharmacie. Êtes-vous sûr·e de vouloir appliquer ces données OCR à votre facture ?")) return;
            }
            applyOcr();
          }}
          className={`w-full text-white gap-2 ${hasHighSeverityWarning ? "bg-amber-600 hover:bg-amber-500" : "bg-emerald-600 hover:bg-emerald-500"}`}
          data-testid="apply-ocr-btn">
          {hasHighSeverityWarning ? <><AlertCircle className="w-4 h-4" /> Appliquer malgré l'alerte de non-concordance</> : <><Sparkles className="w-4 h-4" /> Appliquer les données OCR a la facture</>}
        </Button>
      )}

      {/* Grouped documents */}
      {photos.length > 0 && (
        <div className="flex items-center justify-between mb-3 -mt-2 px-1">
          <p className="text-xs text-slate-500">{photos.length} piece(s) jointe(s) au dossier</p>
          <Button
            onClick={async () => {
              try {
                const res = await axios.get(`${API}/log/invoices/${invoice.id}/photos-zip`, { headers: getHeaders(), responseType: "blob" });
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const a = document.createElement("a");
                a.href = url;
                a.download = `dossier-${invoice.invoice_number || invoice.id}.zip`;
                a.click();
                window.URL.revokeObjectURL(url);
                toast.success("Dossier telecharge en ZIP");
              } catch (e) {
                toast.error("Erreur telechargement ZIP");
              }
            }}
            variant="outline"
            size="sm"
            className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 gap-1.5 text-xs h-7"
            data-testid="download-all-photos-zip"
            title="Telecharger toutes les pieces du dossier en ZIP"
          >
            <Download className="w-3.5 h-3.5" /> Tout telecharger (ZIP)
          </Button>
        </div>
      )}
      {grouped.map(group => (
        <div key={group.value}>
          <div className="flex items-center gap-2 mb-2">
            <group.icon className="w-4 h-4 text-blue-400" />
            <h3 className="text-sm font-semibold text-white">{group.label} ({group.photos.length})</h3>
          </div>
          {group.photos.length === 0 ? (
            <p className="text-sm text-slate-500 py-2 pl-6">Aucun document</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 pl-6">
              {group.photos.map(p => (
                <div key={p.id} className={`bg-slate-950 rounded-xl overflow-hidden border group ${p.ownership_warning ? "border-red-500/60 ring-2 ring-red-500/30" : "border-white/5"}`} data-testid={`photo-card-${p.id}`}>
                  <div className="h-32 bg-slate-800 flex items-center justify-center relative cursor-pointer" onClick={() => photoUrls[p.id] && setViewFullImg(p)}>
                    {photoUrls[p.id] ? <img src={photoUrls[p.id]} alt="" className="w-full h-full object-cover" /> : <Image className="w-8 h-8 text-slate-600" />}
                    {p.ownership_warning && (
                      <div className="absolute top-1 left-1 bg-red-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 shadow-lg" title={p.ownership_message || "Document non concordant"} data-testid={`ownership-badge-${p.id}`}>
                        <AlertCircle className="w-3 h-3" /> Non concordant
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <button onClick={(e) => { e.stopPropagation(); setViewFullImg(p); }} className="p-2 rounded-full bg-blue-600 text-white" title="Agrandir"><Eye className="w-4 h-4" /></button>
                      {p.storage_path && <button onClick={async (e) => { e.stopPropagation(); try { const res = await axios.get(`${API}/log/invoices/${invoice.id}/photos/${p.id}`, { headers: getHeaders(), responseType: "blob" }); const url = URL.createObjectURL(new Blob([res.data])); const a = document.createElement("a"); a.href = url; a.download = p.original_filename || "document"; a.click(); URL.revokeObjectURL(url); } catch { toast.error("Echec du telechargement"); } }} className="p-2 rounded-full bg-sky-600 text-white" title="Telecharger"><Download className="w-4 h-4" /></button>}
                      {p.ocr_status === "completed" && <button onClick={(e) => { e.stopPropagation(); setShowOcrData(p); }} className="p-2 rounded-full bg-emerald-600 text-white" data-testid={`view-ocr-${p.id}`}><Sparkles className="w-4 h-4" /></button>}
                      {(p.ocr_status === "failed" || !p.ocr_status) && p.content_type?.startsWith("image/") && <button onClick={(e) => { e.stopPropagation(); triggerOcr(p.id); }} className="p-2 rounded-full bg-amber-600 text-white"><Scan className="w-4 h-4" /></button>}
                      <button onClick={(e) => { e.stopPropagation(); handleDelete(p.id); }} className="p-2 rounded-full bg-red-600 text-white"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                  <div className="p-2.5">
                    <p className="text-xs text-white truncate">{p.original_filename}</p>
                    <div className="flex items-center justify-between mt-1">
                      <p className="text-xs text-slate-500">{new Date(p.uploaded_at).toLocaleDateString("fr-FR")}</p>
                      {p.ocr_status === "completed" && <span className="inline-flex items-center gap-1 text-xs text-emerald-400"><CheckCircle className="w-3 h-3" /> OK</span>}
                      {(p.ocr_status === "processing" || p.ocr_status === "pending") && <span className="inline-flex items-center gap-1 text-xs text-amber-400"><Loader2 className="w-3 h-3 animate-spin" /> OCR</span>}
                      {p.ocr_status === "failed" && <span className="inline-flex items-center gap-1 text-xs text-red-400"><AlertCircle className="w-3 h-3" /> Echec</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}

      {showOcrData && <OcrDataModal photo={showOcrData} invoiceId={invoice.id} onClose={() => setShowOcrData(null)} />}

      {showArchiveImport && (
        <ArchiveImportModal
          invoiceId={invoice.id}
          currentCategory={category}
          existingPhotoIds={photos.map(p => p.id)}
          onClose={() => setShowArchiveImport(false)}
          onAttached={(attached) => {
            setPhotos(prev => [...prev, ...attached]);
            setShowArchiveImport(false);
            if (attached.length > 0) toast.success(`${attached.length} document(s) rattache(s) au dossier`);
            // refresh parent after a short delay (OCR runs in background)
            setTimeout(() => onRefresh(), 500);
          }}
        />
      )}

      {/* Full-screen image viewer */}
      {viewFullImg && (
        <div className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4" onClick={() => setViewFullImg(null)} data-testid="doc-image-viewer">
          <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
            {viewFullImg.storage_path && (
              <button onClick={async (e) => { e.stopPropagation(); try { const res = await axios.get(`${API}/log/invoices/${invoice.id}/photos/${viewFullImg.id}`, { headers: getHeaders(), responseType: "blob" }); const url = URL.createObjectURL(new Blob([res.data])); const a = document.createElement("a"); a.href = url; a.download = viewFullImg.original_filename || "document"; a.click(); URL.revokeObjectURL(url); } catch { toast.error("Echec du telechargement"); } }} className="text-white bg-sky-500/80 hover:bg-sky-500 p-2 rounded-full" title="Telecharger">
                <Download className="w-5 h-5" />
              </button>
            )}
            <button onClick={() => setViewFullImg(null)} className="text-white bg-white/10 hover:bg-white/20 p-2 rounded-full">
              <X className="w-6 h-6" />
            </button>
          </div>
          <div className="max-w-[90vw] max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
            {photoUrls[viewFullImg.id] ? (
              <img src={photoUrls[viewFullImg.id]} alt={viewFullImg.original_filename} className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl" />
            ) : (
              <div className="bg-slate-900 rounded-xl p-8 text-center">
                <Image className="w-16 h-16 text-slate-600 mx-auto mb-3" />
                <p className="text-sm text-slate-400">Apercu non disponible</p>
              </div>
            )}
            <p className="text-center text-xs text-slate-500 mt-2">{viewFullImg.original_filename} - {viewFullImg.category}</p>
          </div>
        </div>
      )}
    </div>
  );
}

// ---- OCR DATA VIEWER MODAL ----
function OcrDataModal({ photo, invoiceId, onClose }) {
  const [applying, setApplying] = useState(false);
  const data = photo.ocr_data || {};

  const handleApply = async () => {
    setApplying(true);
    try {
      const res = await axios.post(`${API}/log/invoices/${invoiceId}/apply-ocr`, {}, { headers: getHeaders() });
      toast.success(`Données OCR appliquees (${res.data.applied_fields?.length || 0} champs)`);
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setApplying(false); }
  };

  const renderValue = (val) => {
    if (val === null || val === undefined) return <span className="text-slate-500 italic">Non détecté</span>;
    if (typeof val === "number") return <span className="text-emerald-400 font-medium">{new Intl.NumberFormat("fr-FR").format(val)} FCFA</span>;
    if (Array.isArray(val)) {
      return (
        <div className="space-y-1 mt-1">
          {val.map((item, i) => (
            <div key={i} className="bg-slate-800 rounded px-2 py-1 text-[11px]">
              {typeof item === "object" ? Object.entries(item).map(([k, v]) => (
                <span key={k} className="mr-3"><span className="text-slate-500">{k}:</span> <span className="text-white">{v ?? "-"}</span></span>
              )) : String(item)}
            </div>
          ))}
        </div>
      );
    }
    return <span className="text-white">{String(val)}</span>;
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="ocr-data-modal">
      <div className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-lg max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-white/5">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-emerald-400" />
            <h2 className="text-base font-bold text-white">Données OCR extraites</h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="bg-slate-950 rounded-lg p-3 border border-white/5">
            <p className="text-[10px] text-slate-500 mb-1">Photo: {photo.original_filename}</p>
            <p className="text-[10px] text-slate-500">Categorie: {photo.category}</p>
          </div>

          {data.parse_error ? (
            <div className="bg-amber-500/100/10 border border-amber-500/20 rounded-lg p-3">
              <p className="text-xs text-amber-400">L'extraction automatique n'a pas pu structurer les données. Texte brut:</p>
              <p className="text-xs text-white mt-2 whitespace-pre-wrap">{data.raw_text}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {Object.entries(data).filter(([k]) => k !== "notes" && k !== "raw_text" && k !== "parse_error").map(([key, val]) => (
                <div key={key} className="bg-slate-950 rounded-lg px-3 py-2 border border-white/5">
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-0.5">{key.replace(/_/g, " ")}</p>
                  {renderValue(val)}
                </div>
              ))}
              {data.notes && (
                <div className="bg-blue-500/100/5 border border-blue-500/10 rounded-lg px-3 py-2">
                  <p className="text-[10px] text-blue-400 uppercase tracking-wider mb-0.5">Notes</p>
                  <p className="text-xs text-white">{data.notes}</p>
                </div>
              )}
            </div>
          )}

          <Button onClick={handleApply} disabled={applying || data.parse_error} className="w-full bg-emerald-600 hover:bg-emerald-500 text-white gap-2 mt-4" data-testid="apply-ocr-btn">
            {applying ? <><Loader2 className="w-4 h-4 animate-spin" /> Application...</> : <><Sparkles className="w-4 h-4" /> Appliquer les données a la facture</>}
          </Button>
        </div>
      </div>
    </div>
  );
}


// ---- OCR Correction Tab ----
function OcrCorrectionTab({ invoice, onSave, insurers }) {
  const insurersList = insurers || [];
  const [form, setForm] = useState({
    invoice_number: invoice.invoice_number || "",
    insurer_name: invoice.insurer_name || "",
    amount: invoice.amount || "",
    paid_amount: invoice.paid_amount || "",
    date: invoice.date || "",
    patient_name: invoice.patient_name || "",
    description: invoice.description || "",
    period_start: invoice.period_start || "",
    period_end: invoice.period_end || "",
    due_date: invoice.due_date || "",
    ocr_check_amount: invoice.ocr_check_amount || "",
    ocr_check_bank: invoice.ocr_check_bank || "",
    ocr_deposit_amount: invoice.ocr_deposit_amount || "",
  });
  const [affiliations, setAffiliations] = useState(
    (invoice.affiliations || []).map(a => ({ company_name: a.company_name || "", nb_bons: a.nb_bons || 1, amount: a.amount || "" }))
  );
  const [saving, setSaving] = useState(false);
  const [viewImg, setViewImg] = useState(null);

  const addAff = () => setAffiliations(p => [...p, { company_name: "", nb_bons: 1, amount: "" }]);
  const removeAff = (idx) => setAffiliations(p => p.filter((_, i) => i !== idx));
  const updateAff = (idx, field, value) => {
    setAffiliations(p => { const n = [...p]; n[idx] = { ...n[idx], [field]: value }; return n; });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {};
      Object.entries(form).forEach(([k, v]) => { if (v !== "" && v !== null && v !== undefined) payload[k] = k === "amount" || k === "paid_amount" || k === "ocr_check_amount" || k === "ocr_deposit_amount" ? parseFloat(v) || 0 : v; });
      if (affiliations.length > 0) {
        payload.affiliations = affiliations.filter(a => a.company_name).map(a => ({
          company_name: a.company_name,
          nb_bons: parseInt(a.nb_bons) || 1,
          amount: parseFloat(a.amount) || 0,
        }));
      }
      await axios.put(`${API}/log/invoices/${invoice.id}/complete-ocr`, payload, { headers: getHeaders() });
      toast.success("Informations corrigees avec succès");
      onSave();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la sauvegarde");
    } finally { setSaving(false); }
  };

  const downloadImage = (photo) => {
    if (!photo.storage_path) return;
    const a = document.createElement("a");
    a.href = `${API}/files/download/${encodeURIComponent(photo.storage_path)}`;
    a.download = photo.original_filename || "document";
    a.target = "_blank";
    a.click();
  };

  const photos = invoice.photos || [];
  const [photoUrls, setPhotoUrls] = useState({});

  // Load photo blob URLs for display
  useEffect(() => {
    photos.forEach(async (p) => {
      if (photoUrls[p.id]) return;
      try {
        const res = await axios.get(`${API}/log/invoices/${invoice.id}/photos/${p.id}`, { headers: getHeaders(), responseType: "blob" });
        setPhotoUrls(prev => ({ ...prev, [p.id]: URL.createObjectURL(res.data) }));
      } catch { /* ignore */ }
    });
  }, [photos, invoice.id]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="ocr-correction-tab" className="space-y-5">
      {/* Document viewer - clickable thumbnails */}
      {photos.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
            Documents scannés (cliquez pour agrandir)
          </h4>
          <div className="flex flex-wrap gap-2">
            {photos.map((p, i) => (
              <button key={i} onClick={() => setViewImg(p)} data-testid={`photo-thumb-${i}`}
                className="relative w-20 h-20 rounded-lg overflow-hidden border border-white/10 hover:border-sky-500/50 transition-all group">
                {photoUrls[p.id] ? (
                  <img src={photoUrls[p.id]}
                    alt={p.original_filename} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-slate-800">
                    <FileText className="w-6 h-6 text-slate-600" />
                  </div>
                )}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 flex items-center justify-center transition-all">
                  <Eye className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Full-screen image viewer with download */}
      {viewImg && (
        <div className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4" onClick={() => setViewImg(null)} data-testid="image-viewer">
          <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
            <button onClick={(e) => { e.stopPropagation(); downloadImage(viewImg); }} className="text-white bg-sky-500/80 hover:bg-sky-500 p-2 rounded-full" data-testid="download-image-btn" title="Telecharger">
              <Download className="w-5 h-5" />
            </button>
            <button onClick={() => setViewImg(null)} className="text-white bg-white/10 hover:bg-white/20 p-2 rounded-full">
              <X className="w-6 h-6" />
            </button>
          </div>
          <div className="max-w-[90vw] max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
            {photoUrls[viewImg.id] ? (
              <img src={photoUrls[viewImg.id]}
                alt={viewImg.original_filename} className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl" />
            ) : (
              <div className="bg-slate-900 rounded-xl p-8 text-center">
                <FileText className="w-16 h-16 text-slate-600 mx-auto mb-3" />
                <p className="text-sm text-slate-400">Chargement de l'image...</p>
              </div>
            )}
            <p className="text-center text-xs text-slate-500 mt-2">{viewImg.original_filename} - {viewImg.category}</p>
          </div>
        </div>
      )}

      {/* OCR status indicator */}
      <div className={`p-3 rounded-xl border ${invoice.ocr_manually_completed ? "bg-emerald-500/5 border-emerald-500/20" : "bg-amber-500/100/5 border-amber-500/20"}`}>
        <div className="flex items-center gap-2">
          {invoice.ocr_manually_completed ? (
            <>
              <CheckCircle className="w-4 h-4 text-emerald-400" />
              <span className="text-xs text-emerald-400 font-semibold">OCR corrige manuellement</span>
            </>
          ) : (
            <>
              <AlertCircle className="w-4 h-4 text-amber-400" />
              <span className="text-xs text-amber-400 font-semibold">Verifiez et corrigez les informations extraites si necessaire</span>
            </>
          )}
        </div>
      </div>

      {/* Correction form - Main fields */}
      <div>
        <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Informations principales</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Insurer name as dropdown */}
          <div>
            <label className="block text-xs text-slate-400 mb-1.5">Nom de l'assureur</label>
            <select
              data-testid="ocr-field-insurer_name"
              value={form.insurer_name || ""}
              onChange={e => setForm(p => ({ ...p, insurer_name: e.target.value }))}
              className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 transition-all"
            >
              <option value="">-- Choisir un assureur --</option>
              {insurersList.map(ins => (
                <option key={ins.id || ins.name} value={ins.name}>{ins.name}</option>
              ))}
              {form.insurer_name && !insurersList.some(i => i.name === form.insurer_name) && (
                <option value={form.insurer_name}>{form.insurer_name} (actuel)</option>
              )}
            </select>
          </div>
          {[
            { key: "invoice_number", label: "Numéro de facture", type: "text" },
            { key: "amount", label: "Montant total (FCFA)", type: "number" },
            { key: "paid_amount", label: "Montant déjà payé (FCFA)", type: "number" },
            { key: "date", label: "Date de facture", type: "date" },
            { key: "due_date", label: "Date d'échéance", type: "date" },
            { key: "patient_name", label: "Nom du patient / Référence", type: "text" },
            { key: "period_start", label: "Début de période", type: "date" },
            { key: "period_end", label: "Fin de période", type: "date" },
          ].map(f => (
            <div key={f.key}>
              <label className="block text-xs text-slate-400 mb-1.5">{f.label}</label>
              <input
                data-testid={`ocr-field-${f.key}`}
                type={f.type}
                value={form[f.key] || ""}
                onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 transition-all"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Chèque / Remise fields */}
      <div>
        <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Chèque & Remise bancaire</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { key: "ocr_check_amount", label: "Montant du chèque (FCFA)", type: "number" },
            { key: "ocr_check_bank", label: "Banque du chèque", type: "text" },
            { key: "ocr_deposit_amount", label: "Montant remise bancaire (FCFA)", type: "number" },
          ].map(f => (
            <div key={f.key}>
              <label className="block text-xs text-slate-400 mb-1.5">{f.label}</label>
              <input
                data-testid={`ocr-field-${f.key}`}
                type={f.type}
                value={form[f.key] || ""}
                onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 transition-all"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Affiliations editable */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Sociétés affiliées</h4>
          <button onClick={addAff} className="flex items-center gap-1 text-xs text-sky-400 hover:text-sky-300 font-medium" data-testid="add-correction-aff">
            <Plus className="w-3.5 h-3.5" /> Ajouter
          </button>
        </div>
        {affiliations.length === 0 ? (
          <p className="text-xs text-slate-500 text-center py-3 bg-slate-800/50 rounded-lg">Aucune affiliation</p>
        ) : (
          <div className="space-y-2">
            <div className="grid grid-cols-12 gap-2 text-[10px] text-slate-500 uppercase px-1">
              <span className="col-span-5">Société</span>
              <span className="col-span-2 text-center">Bons</span>
              <span className="col-span-4 text-right">Montant (FCFA)</span>
              <span className="col-span-1"></span>
            </div>
            {affiliations.map((aff, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`corr-aff-${idx}`}>
                <input value={aff.company_name} onChange={e => updateAff(idx, "company_name", e.target.value)} placeholder="Nom" className="col-span-5 bg-slate-800 border border-white/10 rounded-lg px-2 py-2 text-sm text-white focus:border-sky-500 focus:outline-none" />
                <input type="number" value={aff.nb_bons} onChange={e => updateAff(idx, "nb_bons", e.target.value)} className="col-span-2 bg-slate-800 border border-white/10 rounded-lg px-2 py-2 text-sm text-white text-center focus:border-sky-500 focus:outline-none" />
                <input type="number" value={aff.amount} onChange={e => updateAff(idx, "amount", e.target.value)} placeholder="0" className="col-span-4 bg-slate-800 border border-white/10 rounded-lg px-2 py-2 text-sm text-white text-right focus:border-sky-500 focus:outline-none" />
                <button onClick={() => removeAff(idx)} className="col-span-1 p-1 rounded hover:bg-red-500/100/100/10 text-slate-500 hover:text-red-400"><X className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <label className="block text-xs text-slate-400 mb-1.5">Description / Notes</label>
        <textarea
          data-testid="ocr-field-description"
          value={form.description || ""}
          onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
          rows={2}
          className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 transition-all"
        />
      </div>

      <Button data-testid="save-ocr-correction" onClick={handleSave} disabled={saving}
        className="w-full bg-sky-500 hover:bg-sky-400 text-white font-semibold gap-2">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
        {saving ? "Sauvegarde..." : "Sauvegarder les corrections"}
      </Button>
    </div>
  );
}


// ---- RECONCILIATION SUGGESTIONS BANNER ----
function ReconciliationBanner({ invoice, onReconciled, onJumpTo }) {
  const [suggestions, setSuggestions] = useState(invoice.reconciliation_suggestions || null);
  const [loading, setLoading] = useState(false);
  const [reconciling, setReconciling] = useState(null);
  const isReconciled = !!invoice.reconciled_with;

  const fetchSuggestions = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/log/invoices/${invoice.id}/reconciliation-suggestions`, { headers: getHeaders() });
      setSuggestions(res.data.suggestions || []);
    } catch {
      toast.error("Erreur recherche suggestions");
    } finally {
      setLoading(false);
    }
  };

  const handleReconcile = async (targetId, targetNumber) => {
    if (!window.confirm(`Confirmer le rapprochement avec la facture ${targetNumber} ?`)) return;
    setReconciling(targetId);
    try {
      const res = await axios.post(`${API}/log/invoices/${invoice.id}/reconcile/${targetId}`, {}, { headers: getHeaders() });
      toast.success(res.data.message);
      onReconciled();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors du rapprochement");
    } finally {
      setReconciling(null);
    }
  };

  if (isReconciled) {
    return (
      <div className="px-5 py-3 bg-emerald-500/10 border-b border-emerald-500/20 shrink-0 flex items-center gap-3" data-testid="recon-done-banner">
        <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
        <div className="flex-1 text-xs text-emerald-300">
          Ce dossier a été rapproché à une facture le {new Date(invoice.reconciled_at).toLocaleDateString("fr-FR")}.
        </div>
      </div>
    );
  }

  const sugList = suggestions === null ? null : suggestions;

  return (
    <div className="px-5 py-3 bg-amber-500/5 border-b border-amber-500/20 shrink-0" data-testid="recon-suggestions-banner">
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-amber-400 flex-shrink-0" />
          <p className="text-xs font-semibold text-amber-300 uppercase tracking-wider">Rapprochement automatique</p>
        </div>
        <Button onClick={fetchSuggestions} disabled={loading} size="sm" variant="outline"
          className="border-amber-500/30 text-amber-300 hover:bg-amber-500/10 gap-1.5 h-7 text-xs" data-testid="recon-refresh-btn">
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          {sugList === null ? "Rechercher" : "Actualiser"}
        </Button>
      </div>

      {sugList === null ? (
        <p className="text-[11px] text-slate-400">Cliquez sur <span className="text-amber-300">Rechercher</span> pour trouver les factures impayées correspondant à ce {invoice.doc_type === "cheque" ? "chèque" : "bordereau"}.</p>
      ) : sugList.length === 0 ? (
        <p className="text-[11px] text-slate-400">Aucune facture correspondante trouvée. Vous pouvez la rattacher manuellement depuis la facture cible.</p>
      ) : (
        <div className="space-y-1.5 max-h-48 overflow-y-auto">
          {sugList.map(s => (
            <div key={s.invoice_id}
              data-testid={`recon-suggestion-${s.invoice_id}`}
              className="flex items-center gap-3 px-3 py-2 bg-slate-950 border border-amber-500/15 hover:border-amber-500/40 rounded-lg transition-colors">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-bold text-white">{s.invoice_number}</span>
                  <span className="text-xs text-slate-400">{s.insurer_name}</span>
                  {s.amount_match && <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300">MONTANT</span>}
                  {s.insurer_match && <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-300">ASSUREUR</span>}
                </div>
                <div className="flex items-center gap-3 text-[10px] text-slate-500 mt-0.5">
                  <span>Restant <span className="text-amber-300 font-bold">{formatFCFA(s.remaining_amount)}</span></span>
                  <span>Écart : <span className="text-cyan-300">±{s.amount_diff_pct}%</span></span>
                  <span>Score : <span className="text-amber-300 font-mono">{s.score}/100</span></span>
                </div>
              </div>
              <Button onClick={() => handleReconcile(s.invoice_id, s.invoice_number)}
                disabled={reconciling === s.invoice_id}
                className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold gap-1.5 h-8 text-xs px-3"
                data-testid={`recon-link-btn-${s.invoice_id}`}>
                {reconciling === s.invoice_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArrowRight className="w-3 h-3" />}
                Lier
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ---- INVERSE RECONCILIATION BANNER (from facture → find cheque) ----
function InverseReconciliationBanner({ invoice, onReconciled }) {
  const [suggestions, setSuggestions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reconciling, setReconciling] = useState(null);

  const fetchSuggestions = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/log/invoices/${invoice.id}/inverse-reconciliation-suggestions`, { headers: getHeaders() });
      setSuggestions(res.data.suggestions || []);
    } catch {
      toast.error("Erreur recherche chèques");
    } finally {
      setLoading(false);
    }
  };

  const handleReconcile = async (chequeId, chequeNumberDossier) => {
    if (!window.confirm(`Confirmer le rapprochement avec le dossier ${chequeNumberDossier} ?`)) return;
    setReconciling(chequeId);
    try {
      const res = await axios.post(`${API}/log/invoices/${chequeId}/reconcile/${invoice.id}`, {}, { headers: getHeaders() });
      toast.success(res.data.message);
      onReconciled();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors du rapprochement");
    } finally {
      setReconciling(null);
    }
  };

  return (
    <div className="px-5 py-3 bg-cyan-500/5 border-b border-cyan-500/20 shrink-0" data-testid="inverse-recon-banner">
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-cyan-400 flex-shrink-0" />
          <p className="text-xs font-semibold text-cyan-300 uppercase tracking-wider">Trouver le chèque correspondant</p>
        </div>
        <Button onClick={fetchSuggestions} disabled={loading} size="sm" variant="outline"
          className="border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/10 gap-1.5 h-7 text-xs" data-testid="inverse-recon-search-btn">
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          {suggestions === null ? "Rechercher" : "Actualiser"}
        </Button>
      </div>
      {suggestions === null ? (
        <p className="text-[11px] text-slate-400">Cliquez sur <span className="text-cyan-300">Rechercher</span> pour balayer vos dossiers chèques/remises non rapprochés et trouver le paiement correspondant.</p>
      ) : suggestions.length === 0 ? (
        <p className="text-[11px] text-slate-400">Aucun chèque non rapproché ne correspond. Importez le chèque reçu via "Importer document (OCR)" puis sélectionnez le type "Chèque".</p>
      ) : (
        <div className="space-y-1.5 max-h-48 overflow-y-auto">
          {suggestions.map(s => (
            <div key={s.cheque_id}
              data-testid={`inverse-recon-${s.cheque_id}`}
              className="flex items-center gap-3 px-3 py-2 bg-slate-950 border border-cyan-500/15 hover:border-cyan-500/40 rounded-lg transition-colors">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-bold text-white">{s.cheque_number_dossier}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-cyan-500/15 text-cyan-300 uppercase">{s.doc_type === "cheque" ? `Chèque ${s.cheque_number ? "n°"+s.cheque_number : ""}` : "Remise"}</span>
                  {s.bank_name && <span className="text-[10px] text-slate-400">{s.bank_name}</span>}
                  {s.amount_match && <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300">MONTANT</span>}
                  {s.insurer_match && <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-300">ASSUREUR</span>}
                </div>
                <div className="flex items-center gap-3 text-[10px] text-slate-500 mt-0.5">
                  <span>Montant <span className="text-cyan-300 font-bold">{formatFCFA(s.amount)}</span></span>
                  <span>Écart : <span className="text-amber-300">±{s.amount_diff_pct}%</span></span>
                  <span>Score : <span className="text-cyan-300 font-mono">{s.score}/100</span></span>
                  {s.issuer && <span>Émetteur : <span className="text-slate-300">{s.issuer}</span></span>}
                </div>
              </div>
              <Button onClick={() => handleReconcile(s.cheque_id, s.cheque_number_dossier)}
                disabled={reconciling === s.cheque_id}
                className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold gap-1.5 h-8 text-xs px-3"
                data-testid={`inverse-recon-link-btn-${s.cheque_id}`}>
                {reconciling === s.cheque_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArrowRight className="w-3 h-3" />}
                Lier
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---- ARCHIVE IMPORT MODAL ----
function ArchiveImportModal({ invoiceId, currentCategory, existingPhotoIds, onClose, onAttached }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [extension, setExtension] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [targetCategory, setTargetCategory] = useState(currentCategory || "facture");
  const [attaching, setAttaching] = useState(false);

  const formatSize = (bytes) => {
    if (!bytes) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
  };

  const existingSet = new Set(existingPhotoIds || []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: 1, limit: 80 };
      if (search) params.search = search;
      if (categoryFilter) params.category = categoryFilter;
      if (extension) params.extension = extension;
      const res = await axios.get(`${API}/log/documents`, { headers: getHeaders(), params });
      setDocs(res.data.documents || []);
    } catch {
      toast.error("Erreur chargement archives");
    } finally {
      setLoading(false);
    }
  }, [search, categoryFilter, extension]);

  useEffect(() => { load(); }, [load]);

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleAttach = async () => {
    if (selected.size === 0) {
      toast.error("Selectionnez au moins un document");
      return;
    }
    setAttaching(true);
    try {
      const res = await axios.post(
        `${API}/log/invoices/${invoiceId}/photos/from-archive`,
        { document_ids: Array.from(selected), category: targetCategory },
        { headers: getHeaders() }
      );
      onAttached(res.data.attached || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors du rattachement");
    } finally {
      setAttaching(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/80 flex items-center justify-center p-4" onClick={onClose} data-testid="archive-import-modal">
      <div className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <FolderOpen className="w-5 h-5 text-blue-400" /> Importer depuis Archives
            </h3>
            <p className="text-xs text-slate-400 mt-0.5">Selectionnez les documents a rattacher a ce dossier facture</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/5 text-slate-400" data-testid="archive-import-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Filters */}
        <div className="px-5 py-3 border-b border-white/5 grid grid-cols-1 md:grid-cols-4 gap-2">
          <div className="relative md:col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher un fichier..."
              className="w-full pl-9 pr-3 py-2 bg-slate-950 border border-white/10 rounded-lg text-white placeholder:text-slate-500 focus:border-blue-500 focus:outline-none text-sm"
              data-testid="archive-import-search" />
          </div>
          <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}
            className="bg-slate-950 border border-white/10 rounded-lg text-slate-300 px-3 py-2 text-sm" data-testid="archive-import-category-filter">
            <option value="">Toutes categories</option>
            <option value="facture">Facture</option>
            <option value="cheque">Chèque</option>
            <option value="remise">Remise</option>
            <option value="ordonnance">Ordonnance</option>
            <option value="bon_commande">Bon commande</option>
            <option value="bon_livraison">Bon livraison</option>
            <option value="general">General</option>
          </select>
          <select value={extension} onChange={e => setExtension(e.target.value)}
            className="bg-slate-950 border border-white/10 rounded-lg text-slate-300 px-3 py-2 text-sm">
            <option value="">Tous formats</option>
            <option value="pdf">PDF</option>
            <option value="xlsx">Excel (xlsx)</option>
            <option value="xls">Excel (xls)</option>
            <option value="jpg">JPG</option>
            <option value="jpeg">JPEG</option>
            <option value="png">PNG</option>
          </select>
        </div>

        {/* Target category */}
        <div className="px-5 py-3 border-b border-white/5 bg-blue-500/5">
          <p className="text-xs text-slate-400 mb-2">Rattacher en tant que :</p>
          <div className="flex flex-wrap gap-2">
            {PHOTO_CATEGORIES.map(cat => (
              <button key={cat.value} onClick={() => setTargetCategory(cat.value)} data-testid={`archive-target-${cat.value}`}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${targetCategory === cat.value ? "bg-blue-600 text-white" : "bg-slate-800 text-slate-400 hover:bg-slate-700"}`}>
                <cat.icon className="w-3.5 h-3.5" /> {cat.label}
              </button>
            ))}
          </div>
        </div>

        {/* Documents list */}
        <div className="flex-1 overflow-y-auto px-5 py-3">
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-blue-400" /></div>
          ) : docs.length === 0 ? (
            <p className="text-slate-500 text-center py-10">Aucun document dans vos archives</p>
          ) : (
            <div className="space-y-1.5">
              {docs.map(doc => {
                const alreadyAttached = existingSet.has(doc.id);
                const isSelected = selected.has(doc.id);
                return (
                  <div key={doc.id} onClick={() => !alreadyAttached && toggleSelect(doc.id)}
                    data-testid={`archive-doc-${doc.id}`}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-all cursor-pointer ${
                      alreadyAttached ? "opacity-50 cursor-not-allowed border-white/5 bg-slate-950" :
                      isSelected ? "border-blue-500 bg-blue-500/10" : "border-white/5 bg-slate-950 hover:border-white/15"
                    }`}>
                    <input type="checkbox" checked={isSelected} disabled={alreadyAttached} readOnly
                      className="w-4 h-4 accent-blue-500 cursor-pointer" />
                    <FileIcon className="w-5 h-5 text-blue-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white truncate font-medium">{doc.original_filename}</p>
                      <div className="flex items-center gap-2 text-[10px] text-slate-500 mt-0.5">
                        <span className="bg-slate-800 px-1.5 py-0.5 rounded uppercase">{doc.extension || "?"}</span>
                        <span>{formatSize(doc.size)}</span>
                        <span>{new Date(doc.created_at).toLocaleDateString("fr-FR")}</span>
                        {doc.linked_invoice_id && doc.linked_invoice_id !== invoiceId && (
                          <span className="text-amber-400">déjà liée à une autre facture</span>
                        )}
                      </div>
                    </div>
                    {alreadyAttached && (
                      <span className="text-[10px] text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded">Déjà rattaché</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-white/10 flex items-center justify-between bg-slate-950">
          <p className="text-xs text-slate-400">
            <span className="font-bold text-blue-400">{selected.size}</span> document(s) selectionne(s)
          </p>
          <div className="flex gap-2">
            <Button onClick={onClose} variant="outline" className="border-white/10 text-slate-300 hover:bg-white/5">
              Annuler
            </Button>
            <Button onClick={handleAttach} disabled={selected.size === 0 || attaching}
              className="bg-blue-600 hover:bg-blue-500 text-white gap-2" data-testid="archive-attach-confirm">
              {attaching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              {attaching ? "Rattachement..." : `Rattacher ${selected.size > 0 ? `(${selected.size})` : ""}`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
